import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import {
  PencilSimpleLineIcon,
  GlobeSimpleIcon,
  UsersIcon
} from "@phosphor-icons/react";

import Icon from "components/Icon/Icon";
import CommonTooltip from "components/Tooltip/index";
import StatusPillButtons from "components/Buttons/StatusPillButtons";

import {
  CompanyNameCell,
  getUserPlaceholder,
  renderValue
} from "utils/renderCompanyCell";
import SocialLinkDropdown from "components/SocialLinkDropdown/index";
import { DEFAULT_PROFILE_IMG } from "utils/helper";

const columns = () => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor("id", {
      header: "ID",
      size: 120,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor((row) => row.name, {
      id: "name",
      header: "Name",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        return (
          <CompanyNameCell
            logo={row?.profile_pic || getUserPlaceholder()}
            name={renderValue(row.name)}
            title={renderValue(row.name)}
          />
        );
      }
    }),
    columnHelper.accessor("email", {
      header: "Email",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),
    columnHelper.accessor("phone", {
      header: "Phone",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("gender", {
      header: "Gender",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("date_of_birth", {
      header: "D.O.B.",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("state_name", {
      header: "State",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("city_name", {
      header: "City",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("managed_by", {
      header: "Managed By",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} />;
      }
    }),
    columnHelper.accessor("is_married", {
      header: "Married",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("have_kids", {
      header: "Kids",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("full_time_profession", {
      header: "Full Time Profession",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("talent_company_id", {
      header: "Talent Company ID",
      size: 160,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("talent_company_name", {
      header: "Company",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>; // optional fallback
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.accessor("ig_name", {
      header: "IG Name",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),

    columnHelper.accessor("ig_handle", {
      header: "IG Handle",
      enableSorting: false,
      cell: (info) => (
        <SocialLinkDropdown value={info.getValue()} type="instagram" />
      )
    }),

    columnHelper.accessor("ig_followers", {
      header: "IG Followers",
      size: 120,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),

    columnHelper.accessor("yt_channel_name", {
      header: "YT Channel Name",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),

    columnHelper.accessor("yt_handle", {
      header: "YT Handle",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const rawValue = info.getValue();

        if (!rawValue || rawValue.trim() === "") {
          return <span>-</span>;
        }

        const cleanedHandle = rawValue
          .replace(/^@/, "") // remove @
          .replace(/^https?:\/\/(www\.)?youtube\.com\//, "") // remove full URL if stored
          .replace(/^channel\//, "")
          .replace(/^@/, "")
          .replace(/\/$/, "");

        // Modern YouTube handle format
        const youtubeUrl = `https://www.youtube.com/@${cleanedHandle}`;

        return (
          <div className="table-link" title={cleanedHandle}>
            <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">
              {youtubeUrl}
            </a>
          </div>
        );
      }
    }),

    columnHelper.accessor("yt_subscribers", {
      header: "YT Subscribers",
      size: 120,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("ln_profile", {
      header: "LinkedIn Profile",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("ln_followers", {
      header: "LinkedIn Followers",
      size: 120,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("content_quality", {
      header: "Content Quality",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("primary_categories", {
      header: "Primary Category",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("sub_categories", {
      header: "Sub Category",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("created_at", {
      header: "Created At",
      size: 200,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("type", {
      header: "Type",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("talent_manager_id", {
      header: "Talent Manager ID",
      size: 160,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("talent_manager_name", {
      header: "Talent Manager",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>; // optional fallback
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.accessor("personal_manager_name", {
      header: "Personal Manager",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>; // optional fallback
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.accessor("personal_manager_email", {
      header: "Personal Manager Email",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>; // optional fallback
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.accessor("personal_manager_phone", {
      header: "Personal Manager Phone",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>; // optional fallback
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.accessor("is_self_managed_verified", {
      header: "Is Self Managed Verified?",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} />;
      }
    }),
    columnHelper.accessor("is_personal_manager_verified", {
      header: "Is Personal Manager Verified?",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} />;
      }
    }),
    columnHelper.accessor("is_hidden", {
      header: "Is Hidden?",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} />;
      }
    }),
    columnHelper.accessor("hidden_reason", {
      header: "Hidden Reason",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell creator-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <span>-</span>;
        return <>{renderValue(value)}</>;
      }
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      size: 80,
      cellClassName: "actions",
      enableSorting: false,
      cell: (info) => {
        const navigate = useNavigate();
        // const companyId = info.row.original.id;
        // const company = info.row.original;

        return (
          <div className="hover-btn-wrapper">
            <CommonTooltip theme="dark" tooltipContent="Edit Details">
              <div
                className="hover-actions"
                onClick={() => {
                  navigate(
                    `/creators/edit/${info.row.original.id}${window.location.search}`,
                    {
                      state: { row: info.row.original }
                    }
                  );
                }}
              >
                <Icon
                  component={PencilSimpleLineIcon}
                  format="outline"
                  weight="regular"
                />
              </div>
            </CommonTooltip>
          </div>
        );
      }
    })
  ];
};

export default columns;
